import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actions from '@/store/slices/user.slice'; // Import your action creators
import * as api from '@/services/user'; // Import your API functions
import { PayloadAction } from '@reduxjs/toolkit';
import { isBoolean } from 'lodash';
import { IPagination } from '../types';

// Define action types
export interface GetUserAction extends PayloadAction<number> {
  type: 'user/getUser';
}

export interface GetUsersAction extends PayloadAction<number> {
  type: 'user/getUsers';
}

export interface CreateUserAction extends PayloadAction<ICreateUser> {
  type: 'user/createUser';
}

export interface UpdateUserAction extends PayloadAction<IUpdateUser> {
  type: 'user/updateUser';
}

export interface DeleteUserAction extends PayloadAction<IDeleteUser> {
  type: 'user/deleteUser';
}
export interface SelectedRole {
  name: string;
  id: string;
}

// Define the shape of the API response for a single provider
export interface SingleUserResponse {
  userId: string;
  email: string;
  authUserId: string;
  firstName?: string;
  lastName?: string;
  roleId: string;
  createdOn?: string;
  updatedOn?: string;
  // ... other properties
}
export interface IUser {
  userId: string;
  email: string;
  authUserId: string;
  firstName?: string;
  lastName?: string;
  roleId: string;
  createdOn?: string;
  updatedOn?: string;
}

export interface ICreateUser {
  email: string;
  firstName: string;
  lastName: string;
  roleId: string;
}

export interface IUpdateUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roleId: string;
}

export interface IDeleteUser {
  userId: string;
}

export interface ApiResponse {
  success: boolean;
  message: string;
  data: IUser[];
  pagination: IPagination;
}

export interface ILimit {
  page?: number;
  limit?: number;
}

function* getUsers(action: GetUsersAction): Generator {
  try {
    const response: any = yield call(api.fetchUsers as any, action?.payload);
    if (isValidUsersResponse(response?.data)) {
      yield put(actions.getUsersSuccess(response?.data?.data));
    } else {
      yield put(actions.getUsersFailure(response?.data));
    }
  } catch (error: any) {
    yield put(
      actions.getUsersFailure(
        error?.response?.data || { message: error?.message },
      ),
    );
  }
}

// Your saga function
function* getUser(action: GetUserAction): Generator {
  try {
    const response: any = yield call(api.fetchUser, action?.payload);
    const res = response?.data?.data;

    if (isValidUserResponse(res)) {
      yield put({
        type: 'user/getUserSuccess',
        payload: res as SingleUserResponse,
      });
    } else {
      yield put({ type: 'user/getUserFailure' });
    }
  } catch (error: any) {
    yield put({
      type: 'user/getUserFailure',
      payload: error?.response?.data || { message: error?.message },
    });
  }
}

function* createUser(action: CreateUserAction) {
  try {
    const response: unknown = yield call(api.createUser as any, action);
    if (isValidUserResponse(response)) {
      yield put({
        type: 'user/createUserSuccess',
        payload: response as SingleUserResponse,
      });
    } else {
      yield put({
        type: 'user/createUserFailure',
        payload: (response as any)?.data,
      });
    }
  } catch (error: any) {
    yield put({
      type: 'user/createUserFailure',
      payload: error?.response?.data || { message: error?.message },
    });
  }
}

function* updateUser(action: UpdateUserAction) {
  try {
    const response: unknown = yield call(
      api.updateUser,
      action?.payload?.id,
      action?.payload,
    );
    if (isValidUserResponse(response)) {
      yield put({
        type: 'user/updateUserSuccess',
        payload: response as SingleUserResponse,
      });
    } else {
      yield put({ type: 'user/updateUserFailure', payload: response });
    }
  } catch (error) {
    yield put({
      type: 'user/updateUserFailure',
      payload: 'Something went wrong',
    });
  }
}

function* deleteUser(action: DeleteUserAction) {
  try {
    const response: unknown = yield call(api.deleteUser, action?.payload);
    if (isValidUserResponse(response)) {
      yield put({
        type: 'user/deleteUserSuccess',
        payload: response as SingleUserResponse,
      });
    } else {
      yield put({
        type: 'user/deleteUserFailure',
        payload: (response as any)?.data,
      });
    }
  } catch (error: any) {
    yield put({
      type: 'user/deleteUserFailure',
      payload: error?.response?.data || { message: error?.message },
    });
  }
}

function isValidUserResponse(
  response: unknown,
): response is SingleUserResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'userId' in response &&
    'firstName' in response &&
    'lastName' in response &&
    'authUserId' in response &&
    'email' in response &&
    'roleId' in response &&
    'createdOn' in response &&
    'updatedOn' in response
    // Add other properties if present in the API response
  );
}

function isValidUsersResponse(response: ApiResponse): boolean {
  return (
    isBoolean(response.success) &&
    response.message === 'Successfully got list of Users.'
  );
}

function* userSaga(): Generator {
  // Watch for the specific action type
  yield takeLatest<GetUsersAction>('user/getUsers', getUsers);
  yield takeEvery<GetUserAction>('user/getUser', getUser);
  yield takeEvery<CreateUserAction>('user/createUser', createUser);
  yield takeEvery<UpdateUserAction>('user/updateUser', updateUser);
  yield takeEvery<DeleteUserAction>('user/deleteUser', deleteUser);
}

export default userSaga;
